import { useMemo } from 'react';
import { Engagement, Reference } from 'sr-types/lib/search/v1/graphql';
import type { EntitySecurity } from '../common/auth/Security';
import { useGetPersonDetails } from '../common/components/person/person';
import { ENTITY_CONSTANTS } from '../common/constants/entityConstants';
import { useProductionDetailsData } from '../production/helpers/productionApi';
import { createUpdatedProductionItem } from '../production/helpers/productionUtils';

type MarketplaceDisplayItem = {
    reference?: Reference;
    security?: EntitySecurity;
};

type UseMarketplaceEngagementDetailsParams = {
    clickedPerson: string | null;
    selectedItem: MarketplaceDisplayItem | null;
    clickedEngagement: Engagement | null;
    detailsVisible: boolean;
    skipProductionFetch?: boolean;
};

export function useMarketplaceEngagementDetails({
    clickedPerson,
    selectedItem,
    clickedEngagement,
    detailsVisible,
    skipProductionFetch = false
}: UseMarketplaceEngagementDetailsParams) {
    const { personDetails: clickedPersonDetailsData, personDetailsLoading: clickedPersonLoading } = useGetPersonDetails(
        clickedPerson,
        !clickedPerson
    );

    const displayItem = useMemo(
        () =>
            clickedPerson && clickedPersonDetailsData?.person
                ? createUpdatedProductionItem(clickedPersonDetailsData.person)
                : selectedItem,
        [clickedPerson, clickedPersonDetailsData?.person, selectedItem]
    );

    const engagementProductionId = useMemo(
        () =>
            clickedEngagement?.baseEntity?.type === ENTITY_CONSTANTS.production
                ? clickedEngagement?.baseEntity?.id
                : undefined,
        [clickedEngagement]
    );

    const { productionDetailsData, productionDetailsLoading } = useProductionDetailsData(
        engagementProductionId,
        !engagementProductionId || skipProductionFetch
    );

    const showEngagementProductionDetails = useMemo(
        () => Boolean(engagementProductionId) && detailsVisible && !skipProductionFetch,
        [engagementProductionId, detailsVisible, skipProductionFetch]
    );

    const noteId =
        clickedEngagement?.baseEntity?.type === ENTITY_CONSTANTS.note ? clickedEngagement.baseEntity.id : undefined;

    const showNoteDetails = Boolean(noteId) && detailsVisible && Boolean(displayItem?.reference?.id);

    const noteEntityReference = showNoteDetails ? displayItem?.reference : undefined;

    return {
        clickedPersonLoading,
        displayItem,
        productionDetailsData,
        productionDetailsLoading,
        showEngagementProductionDetails,
        noteId,
        showNoteDetails,
        noteEntityReference
    };
}
